import React, { useEffect, useState } from "react";
import { Sunrise, Sun, Moon, Send, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { PageHero, Section, Badge, Button } from "@/components/premium/UI";
import { Pattern } from "@/components/premium/Patterns";
import { toast } from "sonner";

const SLOTS = [
  { key: "morning", label: "Morning brief",  time: "06:00", icon: Sunrise, tone: "accent" },
  { key: "midday",  label: "Midday brief",   time: "13:00", icon: Sun,     tone: "primary" },
  { key: "evening", label: "Evening brief",  time: "20:00", icon: Moon,    tone: "info" },
];

export default function Briefs() {
  const { has } = usePermissions();
  const canSend = has("admin:settings:manage");
  const [slot, setSlot] = useState("morning");
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  async function load(k = slot) {
    setLoading(true);
    try { const r = await api.get(`/briefs/${k}`); setBrief(r.data); }
    catch { setBrief(null); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(slot); /* eslint-disable-next-line */ }, [slot]);

  async function send() {
    setSending(true);
    try { const r = await api.post(`/briefs/${slot}/send`, { channels: ["in_app", "push"] }); toast.success(`Delivered to ${r.data.delivered_to} recipient(s)`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  }

  return (
    <div className="space-y-6" data-testid="briefs-page">
      <PageHero
        eyebrow="Executive Briefs"
        title="Automatic homestead digests"
        subtitle="Role-aware briefs composed from live data + active intelligence. Delivered to every authorised recipient across in-app, push, email and (when configured) WhatsApp."
        moduleKind="situation"
        actions={canSend && <Button variant="primary" onClick={send} disabled={sending} data-testid="brief-send-btn"><Send className="w-4 h-4" /> {sending ? "Sending…" : "Send now"}</Button>}
      />

      <div className="flex flex-wrap gap-2">
        {SLOTS.map(s => (
          <button
            key={s.key}
            onClick={() => setSlot(s.key)}
            data-testid={`brief-slot-${s.key}`}
            className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-3 transition-colors ${slot === s.key ? "border-primary/50 bg-primary/5" : "border-border hover:bg-muted/40"}`}
          >
            <s.icon className="w-4 h-4" />
            <div className="text-left leading-tight">
              <div className="text-sm font-semibold">{s.label}</div>
              <div className="text-[11px] text-muted-foreground">{s.time}</div>
            </div>
          </button>
        ))}
      </div>

      <Section className="relative overflow-hidden" title={brief ? `${brief.greeting} · ${brief.property}` : "Loading brief"} description={brief ? `${brief.slot.charAt(0).toUpperCase() + brief.slot.slice(1)} focus: ${brief.focus}` : ""}>
        <Pattern kind="situation" />
        <div className="relative">
          {loading ? <div className="animate-pulse space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-16 bg-muted/40 rounded-xl" />)}</div>
          : !brief ? <div className="text-muted-foreground">No brief available.</div>
          : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="brief-sections">
                {(brief.sections || []).map((s, i) => (
                  <div key={i} className="rounded-2xl border border-border p-4 bg-card">
                    <div className="eyebrow mb-1.5">{s.heading}</div>
                    <div className="text-sm text-foreground">{s.body}</div>
                    {s.alerts?.length ? <div className="mt-2 flex gap-1 flex-wrap">{s.alerts.map((a, j) => <Badge key={j} variant="pending">{a}</Badge>)}</div> : null}
                  </div>
                ))}
              </div>

              {(brief.priorities || []).length > 0 && (
                <div>
                  <div className="eyebrow mb-2">Priorities</div>
                  <ul className="space-y-2" data-testid="brief-priorities">
                    {brief.priorities.map(p => (
                      <li key={p.id} className="rounded-xl border border-border p-3 flex items-start gap-3 bg-card">
                        <AlertTriangle className={`w-4 h-4 mt-0.5 ${p.priority === "critical" ? "text-destructive" : "text-accent"}`} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{p.observation}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{p.recommendation}</div>
                        </div>
                        <div className="ml-auto flex gap-1">
                          <Badge variant={p.priority === "critical" ? "critical" : "pending"}>{p.priority}</Badge>
                          <Badge variant="neutral">{p.module}</Badge>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="text-[11px] text-muted-foreground pt-2 border-t border-border">
                Generated {brief.generated_at?.slice(0, 16).replace("T", " ")} · Slot {brief.slot_time}
              </div>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
